import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * N-4-7：時間的複雜計算（四上）。
 * 四上第七章：時間換算（時↔分↔秒）、跨天時間差、日期計算。
 *
 * difficulty 1：時間換算（x 小時 y 分 = ? 分鐘）
 * difficulty 2：跨整點時間差（多段加減，含進位）
 * difficulty 3：日期換算（週/日 ↔ 天數）與時間計算
 */
export function generateTimeComplex(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);
  const qType = randInt(rand, 0, difficulty === 3 ? 2 : 1);

  if (difficulty === 1 || qType === 0) {
    // 時間換算: x 時 y 分 → 總分鐘數
    const hours = randInt(rand, 1, difficulty === 1 ? 3 : 5);
    const minutes = randInt(rand, 0, 11) * 5; // 0,5,10..55
    const totalMin = hours * 60 + minutes;
    const correct = String(totalMin);
    const d1 = String(totalMin + 5);
    const d2 = String(totalMin - 5 > 0 ? totalMin - 5 : totalMin + 10);
    const d3 = String(hours * 60); // forgot to add minutes
    const options = [correct, d1, d2, d3];
    const shuffled = [...options].sort((x, y) => mulberry32(seed + parseInt(x) + 3)() - mulberry32(seed + parseInt(y) + 3)());
    return {
      kind: 'mcq',
      prompt: `${hours} 小時 ${minutes} 分鐘，共幾分鐘？`,
      options: shuffled,
      correctAnswer: shuffled.indexOf(correct),
      explanation: `${hours} 小時 = ${hours * 60} 分鐘，加上 ${minutes} 分鐘，共 ${totalMin} 分鐘。`,
      kp: 'math.time.complex.4up',
    };
  }

  if (qType === 1) {
    // 跨整點時間差（多段）
    const startH = randInt(rand, 7, 12);
    const startM = randInt(rand, 0, 11) * 5;
    const endH = randInt(rand, startH + 1, Math.min(startH + 4, 18));
    const endM = randInt(rand, 0, 11) * 5;
    const elapsedMin = (endH - startH) * 60 + (endM - startM);
    const correct = String(elapsedMin);
    const d1 = String(elapsedMin + 10);
    const d2 = String(elapsedMin - 10 > 0 ? elapsedMin - 10 : elapsedMin + 5);
    const d3 = String((endH - startH) * 60);
    const options = [correct, d1, d2, d3];
    const shuffled = [...options].sort((x, y) => mulberry32(seed + parseInt(x) + 7)() - mulberry32(seed + parseInt(y) + 7)());
    return {
      kind: 'mcq',
      prompt: `從 ${startH}:${pad2(startM)} 到 ${endH}:${pad2(endM)}，經過幾分鐘？`,
      options: shuffled,
      correctAnswer: shuffled.indexOf(correct),
      explanation: `${endH}:${pad2(endM)} − ${startH}:${pad2(startM)} = ${elapsedMin} 分鐘。`,
      kp: 'math.time.complex.4up',
    };
  }

  // qType === 2: 週/日換算
  const weeks = randInt(rand, 2, 8);
  const extraDays = randInt(rand, 0, 6);
  const totalDays = weeks * 7 + extraDays;
  const correct = String(totalDays);
  const d1 = String(totalDays + 7);
  const d2 = String(weeks * 7);
  const d3 = String(totalDays - 7 > 0 ? totalDays - 7 : totalDays + 1);
  const options = [correct, d1, d2, d3];
  const shuffled = [...options].sort((x, y) => mulberry32(seed + parseInt(x) + 13)() - mulberry32(seed + parseInt(y) + 13)());
  return {
    kind: 'mcq',
    prompt: `${weeks} 週 ${extraDays} 天，共幾天？`,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation: `${weeks} 週 = ${weeks * 7} 天，加上 ${extraDays} 天，共 ${totalDays} 天。`,
    kp: 'math.time.complex.4up',
  };
}
